import {
  CONNECTOR_AUTHS,
  CONNECTOR_INTERVALS,
  IMPORT_KINDS,
  type ConnectorAuth,
  type ConnectorFormat,
  type ImportKind,
} from '@beta-assur/shared';
import { Link } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { ConfirmButton } from '../../components/confirm';
import { Field } from '../../components/page';
import { ActionError, Empty, QueryView } from '../../components/states';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Checkbox, Input, Select } from '../../components/ui/input';
import { Table, TBody, TD, TH, THead, TR } from '../../components/ui/table';
import { instant } from '../../lib/format';
import { useI18n } from '../../lib/i18n';
import { useApiMutation, useApiQuery } from '../../lib/query';
import { useTenant } from '../../lib/referentials';
import type { Connector, ConnectorRun, ConnectorTest } from '../../lib/types';

// Connecteur SI en lecture seule (BA-094), admins : une source par type de données, lue à
// intervalle régulier en GET seul. Lecture d'essai pour régler la correspondance des colonnes,
// lecture immédiate, journal des passages. Le secret d'accès n'est jamais réaffiché.

const STATUS_TONE = { ok: 'success', empty: 'neutral', error: 'danger' } as const;

interface Draft {
  format: ConnectorFormat;
  url: string;
  auth: ConnectorAuth;
  headerName: string;
  secret: string;
  itemsPath: string;
  sinceParam: string;
  intervalMinutes: number;
}

const draftOf = (c: Connector | undefined): Draft => ({
  format: c?.format ?? 'json',
  url: c?.url ?? '',
  auth: c?.auth ?? 'none',
  headerName: c?.headerName ?? '',
  secret: '',
  itemsPath: c?.itemsPath ?? '',
  sinceParam: c?.sinceParam ?? '',
  intervalMinutes: c?.intervalMinutes ?? 60,
});

function SourceForm({
  kind,
  connector,
  onDone,
}: {
  kind: ImportKind;
  connector?: Connector;
  onDone: () => void;
}) {
  const t = useI18n().t;
  const c = t.connectors;
  const [draft, setDraft] = useState<Draft>(() => draftOf(connector));
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));
  const save = useApiMutation<Record<string, unknown>, Connector>(
    connector ? 'PATCH' : 'POST',
    connector ? `/v1/connectors/${connector.id}` : '/v1/connectors',
    { invalidate: ['/v1/connectors'], onSuccess: onDone },
  );

  function submit(event: FormEvent) {
    event.preventDefault();
    save.mutate({
      ...(connector ? {} : { kind }),
      format: draft.format,
      url: draft.url.trim(),
      auth: draft.auth,
      headerName: draft.auth === 'header' ? draft.headerName.trim() : null,
      ...(draft.secret ? { secret: draft.secret } : {}),
      itemsPath: draft.format === 'json' && draft.itemsPath.trim() ? draft.itemsPath.trim() : null,
      sinceParam: draft.sinceParam.trim() || null,
      intervalMinutes: draft.intervalMinutes,
    });
  }

  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
      <Field label={c.url} hint={c.urlHint} className="sm:col-span-2">
        <Input
          type="url"
          required
          dir="ltr"
          value={draft.url}
          placeholder="https://"
          onChange={(e) => set('url', e.target.value)}
        />
      </Field>
      <Field label={c.format}>
        <Select
          value={draft.format}
          onChange={(e) => set('format', e.target.value as ConnectorFormat)}
        >
          <option value="json">{c.formats.json}</option>
          <option value="csv">{c.formats.csv}</option>
        </Select>
      </Field>
      <Field label={c.interval}>
        <Select
          value={draft.intervalMinutes}
          onChange={(e) => set('intervalMinutes', Number(e.target.value))}
        >
          {CONNECTOR_INTERVALS.map((n) => (
            <option key={n} value={n}>
              {c.intervals[n]}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={c.auth}>
        <Select value={draft.auth} onChange={(e) => set('auth', e.target.value as ConnectorAuth)}>
          {CONNECTOR_AUTHS.map((a) => (
            <option key={a} value={a}>
              {c.auths[a]}
            </option>
          ))}
        </Select>
      </Field>
      {draft.auth === 'header' ? (
        <Field label={c.headerName}>
          <Input
            dir="ltr"
            value={draft.headerName}
            placeholder="X-Api-Key"
            onChange={(e) => set('headerName', e.target.value)}
          />
        </Field>
      ) : null}
      {draft.auth !== 'none' ? (
        <Field
          label={draft.auth === 'basic' ? c.secretBasic : c.secret}
          hint={connector?.hasSecret ? c.secretKept : c.secretHint}
        >
          <Input
            type="password"
            autoComplete="off"
            dir="ltr"
            value={draft.secret}
            onChange={(e) => set('secret', e.target.value)}
          />
        </Field>
      ) : null}
      {draft.format === 'json' ? (
        <Field label={c.itemsPath} hint={c.itemsPathHint}>
          <Input
            dir="ltr"
            value={draft.itemsPath}
            placeholder="data.items"
            onChange={(e) => set('itemsPath', e.target.value)}
          />
        </Field>
      ) : null}
      <Field label={c.sinceParam} hint={c.sinceParamHint}>
        <Input
          dir="ltr"
          value={draft.sinceParam}
          placeholder="updated_since"
          onChange={(e) => set('sinceParam', e.target.value)}
        />
      </Field>
      <div className="flex items-center gap-2 sm:col-span-2">
        <Button type="submit" disabled={save.isPending || !draft.url.trim()}>
          {connector ? c.save : c.create}
        </Button>
        {connector ? (
          <Button type="button" variant="ghost" onClick={onDone}>
            {t.common.cancel}
          </Button>
        ) : null}
      </div>
      <ActionError error={save.error} />
    </form>
  );
}

/** Résultat de la lecture d'essai : aperçu et correspondance des colonnes à enregistrer. */
function MappingEditor({ connector, test }: { connector: Connector; test: ConnectorTest }) {
  const t = useI18n().t;
  const c = t.connectors;
  const [mapping, setMapping] = useState<Record<string, string>>(test.suggestedMapping);
  const save = useApiMutation<{ mapping: Record<string, string> }, Connector>(
    'PATCH',
    `/v1/connectors/${connector.id}`,
    { invalidate: ['/v1/connectors'] },
  );
  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm">{c.testResult(test.rowsTotal, test.httpStatus)}</p>
      {test.preview.length ? (
        <div className="max-h-64 overflow-auto rounded-lg border border-border">
          <Table>
            <THead>
              <tr>
                {test.headers.map((h) => (
                  <TH key={h} className="whitespace-nowrap font-mono text-xs">
                    {h}
                  </TH>
                ))}
              </tr>
            </THead>
            <TBody>
              {test.preview.slice(0, 5).map((row, i) => (
                <TR key={i}>
                  {row.map((v, j) => (
                    <TD key={j} className="whitespace-nowrap text-xs">
                      {v}
                    </TD>
                  ))}
                </TR>
              ))}
            </TBody>
          </Table>
        </div>
      ) : null}
      <fieldset className="grid gap-2 sm:grid-cols-2">
        <legend className="mb-1 text-sm font-medium">{t.imports.mapping}</legend>
        {test.fields.map((f) => (
          <Field key={f.key} label={f.required ? `${f.label} *` : f.label}>
            <Select
              value={mapping[f.key] ?? ''}
              onChange={(e) =>
                setMapping((m) => {
                  const next = { ...m };
                  if (e.target.value) next[f.key] = e.target.value;
                  else delete next[f.key];
                  return next;
                })
              }
            >
              <option value="">{t.imports.ignore}</option>
              {test.headers.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </Select>
          </Field>
        ))}
      </fieldset>
      <div>
        <Button onClick={() => save.mutate({ mapping })} disabled={save.isPending}>
          {c.saveMapping}
        </Button>
      </div>
      {save.isSuccess ? (
        <p role="status" className="text-sm text-success">
          {c.mappingSaved}
        </p>
      ) : null}
      <ActionError error={save.error} />
    </div>
  );
}

function Runs({ connector }: { connector: Connector }) {
  const { t, language } = useI18n();
  const tz = useTenant().data?.timezone;
  const c = t.connectors;
  const runs = useApiQuery<{ items: ConnectorRun[] }>(`/v1/connectors/${connector.id}/runs`);
  return (
    <QueryView query={runs}>
      {(data) =>
        data.items.length === 0 ? (
          <Empty>{c.noRuns}</Empty>
        ) : (
          <Table>
            <THead>
              <tr>
                <TH>{c.runAt}</TH>
                <TH>{c.trigger}</TH>
                <TH>{c.status}</TH>
                <TH className="text-end">{c.rows}</TH>
                <TH>{c.detail}</TH>
              </tr>
            </THead>
            <TBody>
              {data.items.slice(0, 10).map((r) => (
                <TR key={r.id}>
                  <TD className="whitespace-nowrap">{instant(r.startedAt, tz, language)}</TD>
                  <TD>{c.triggers[r.trigger]}</TD>
                  <TD>
                    <Badge tone={STATUS_TONE[r.status]}>{c.statuses[r.status]}</Badge>
                  </TD>
                  <TD className="text-end tabular-nums">{r.rows}</TD>
                  <TD className="text-sm">
                    {r.batchId ? (
                      <Link
                        to="/imports/$id"
                        params={{ id: r.batchId }}
                        className="text-primary hover:underline"
                      >
                        {c.batch}
                      </Link>
                    ) : (
                      (r.error ?? '-')
                    )}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )
      }
    </QueryView>
  );
}

function ConnectorCard({
  kind,
  connector,
}: {
  kind: ImportKind;
  connector: Connector | undefined;
}) {
  const { t, language } = useI18n();
  const tz = useTenant().data?.timezone;
  const c = t.connectors;
  const [editing, setEditing] = useState(false);
  const [test, setTest] = useState<ConnectorTest | null>(null);
  const invalidate = ['/v1/connectors'];
  const toggle = useApiMutation<{ enabled: boolean }, Connector>(
    'PATCH',
    `/v1/connectors/${connector?.id ?? ''}`,
    { invalidate },
  );
  const tryRead = useApiMutation<void, ConnectorTest>(
    'POST',
    `/v1/connectors/${connector?.id ?? ''}/test`,
    { onSuccess: setTest },
  );
  const runNow = useApiMutation<void, { queued: true }>(
    'POST',
    `/v1/connectors/${connector?.id ?? ''}/run`,
    { invalidate },
  );
  const remove = useApiMutation<void>('DELETE', `/v1/connectors/${connector?.id ?? ''}`, {
    invalidate,
  });

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle>{t.imports.kinds[kind]}</CardTitle>
        {connector ? (
          <div className="flex items-center gap-2">
            {connector.lastStatus ? (
              <Badge tone={STATUS_TONE[connector.lastStatus]}>
                {c.statuses[connector.lastStatus]}
              </Badge>
            ) : null}
            <Badge tone={connector.enabled ? 'success' : 'neutral'}>
              {connector.enabled ? c.enabled : c.disabled}
            </Badge>
          </div>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {!connector || editing ? (
          <SourceForm
            kind={kind}
            {...(connector ? { connector } : {})}
            onDone={() => setEditing(false)}
          />
        ) : (
          <>
            <dl className="grid gap-x-4 gap-y-1 text-sm sm:grid-cols-[auto_1fr]">
              <dt className="text-muted-foreground">{c.url}</dt>
              <dd className="break-all font-mono text-xs" dir="ltr">
                {connector.url}
              </dd>
              <dt className="text-muted-foreground">{c.format}</dt>
              <dd>
                {c.formats[connector.format]}, {c.intervals[connector.intervalMinutes as 60]}
              </dd>
              <dt className="text-muted-foreground">{c.auth}</dt>
              <dd>{c.auths[connector.auth]}</dd>
              <dt className="text-muted-foreground">{c.lastRun}</dt>
              <dd>
                {connector.lastRunAt ? instant(connector.lastRunAt, tz, language) : c.never}
                {connector.lastError ? (
                  <span className="block text-destructive">{connector.lastError}</span>
                ) : null}
              </dd>
            </dl>
            {connector.missing.length ? (
              <p role="alert" className="text-sm text-warning">
                {c.missing}
              </p>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={connector.enabled}
                  disabled={toggle.isPending}
                  onChange={(e) => toggle.mutate({ enabled: e.target.checked })}
                />
                {c.activate}
              </label>
              <Button
                variant="outline"
                onClick={() => tryRead.mutate()}
                disabled={tryRead.isPending}
              >
                {c.test}
              </Button>
              <Button
                variant="outline"
                onClick={() => runNow.mutate()}
                disabled={runNow.isPending || connector.missing.length > 0}
              >
                {c.runNow}
              </Button>
              <Button variant="ghost" onClick={() => setEditing(true)}>
                {c.edit}
              </Button>
              <ConfirmButton
                label={c.remove}
                title={c.remove}
                message={c.removeMessage}
                variant="destructive"
                onConfirm={() => remove.mutateAsync()}
              />
            </div>
            {runNow.isSuccess ? (
              <p role="status" className="text-sm text-success">
                {c.queued}
              </p>
            ) : null}
            <ActionError error={tryRead.error ?? runNow.error ?? toggle.error} />
            {test ? (
              <MappingEditor key={JSON.stringify(test.headers)} connector={connector} test={test} />
            ) : null}
            <div>
              <h3 className="mb-2 text-sm font-medium">{c.journal}</h3>
              <Runs connector={connector} />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function ConnectorsSection() {
  const t = useI18n().t;
  const list = useApiQuery<{ items: Connector[] }>('/v1/connectors');
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-muted-foreground">{t.connectors.intro}</p>
      <QueryView query={list}>
        {(data) => (
          <>
            {IMPORT_KINDS.map((kind) => (
              <ConnectorCard
                key={kind}
                kind={kind}
                connector={data.items.find((c) => c.kind === kind)}
              />
            ))}
          </>
        )}
      </QueryView>
    </div>
  );
}
